import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PaidMediaPromptApiFactory } from "@/apis/brand-os/paid-media-prompts";
import type { SavedPrompt, SavedPromptInput } from "@/schema/brand-os/paid-media-prompts";

/**
 * Saved prompts powering the saved-prompts drawer next to the
 * Insights & Recommendations composer.
 *
 * Returns `[]` for a fresh user — the drawer renders an empty state.
 * Most prompts arrive here via `POST /insights/run`'s implicit auto-save,
 * not via the `Create` API.
 */
export function usePaidMediaPrompts(userId: string | null | undefined) {
  const query = useQuery({
    ...PaidMediaPromptApiFactory.Queries.List(userId ?? ""),
    enabled: Boolean(userId),
  });
  return {
    prompts: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

interface PromptMutationsOpts {
  /** Fired after a successful create — receives the new prompt id. */
  onCreated?: (prompt: SavedPrompt) => void;
  /** Fired after a successful edit. Useful for triggering an attention regen. */
  onUpdated?: (prompt: SavedPrompt) => void;
  /** Fired after a successful delete — receives the deleted id. */
  onDeleted?: (promptId: string) => void;
}

/**
 * CRUD mutations for the prompts list. Each mutation invalidates the list
 * query on success so the drawer auto-updates. `Create` is rarely used in
 * the new flow — `/insights/run` auto-saves — so the typical consumer
 * surface here is `update` (rename) and `deleteMut` (housekeeping).
 */
export function usePaidMediaPromptMutations(
  userId: string | null | undefined,
  opts: PromptMutationsOpts = {},
) {
  const queryClient = useQueryClient();
  const listKey = PaidMediaPromptApiFactory.Keys.List(userId ?? "");

  const invalidateList = useCallback(
    () => queryClient.invalidateQueries({ queryKey: listKey }),
    [queryClient, listKey],
  );

  const createMutation = useMutation({
    mutationFn: (body: SavedPromptInput) => {
      if (!userId) return Promise.reject(new Error("Missing user_id"));
      return PaidMediaPromptApiFactory.Apis.Create(userId, body);
    },
    onSuccess: (prompt) => {
      invalidateList();
      opts.onCreated?.(prompt);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ promptId, body }: { promptId: string; body: SavedPromptInput }) => {
      if (!userId) return Promise.reject(new Error("Missing user_id"));
      return PaidMediaPromptApiFactory.Apis.Update(promptId, userId, body);
    },
    onSuccess: (prompt) => {
      invalidateList();
      opts.onUpdated?.(prompt);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (promptId: string) => {
      if (!userId) return Promise.reject(new Error("Missing user_id"));
      return PaidMediaPromptApiFactory.Apis.Delete(promptId, userId);
    },
    onSuccess: (_void, promptId) => {
      invalidateList();
      opts.onDeleted?.(promptId);
    },
  });

  return {
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    update: updateMutation.mutate,
    updateAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteMut: deleteMutation.mutate,
    deleteAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
